import React, { useState, useEffect, useContext } from "react";

import Tippy from "@tippyjs/react";
import { Link, useNavigate } from "react-router-dom";

import useClickOutside from "../../utils/CustomHooks/useClickOutside";
import { getRoles } from "../../utils/OktaHelpers";
import {
    LABSTORE_ACCESS,
    ORGANIZATION_TYPES,
    ROLES,
} from "../../utils/constants";
import {
    DASHBOARD_ROUTES,
    LABSTORE_ROUTES,
    MODEL_DEVELOPMENT_ROUTES,
    SCANS_MANAGEMENT_ROUTES,
    ACCESS_MANAGEMENT_ROUTES,
    LABLAKE_ROUTES,
    MATERIALS_ROUTES,
    OPTIONS_ROUTES,
    EXTERNAL_ROUTES,
    GLOBAL_ROUTES,
} from "../../utils/RoutesConstant";
import { isAuthorized, isLabstoreAuthorized } from "../../utils/RolesHelpers";
import { t } from "i18next";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import { isEmpty } from "lodash";
import { UserContext } from "../../lib/MainContext";
import { getURIParts, kebabToCamelCase } from "../../utils/Helpers";
import BuchiLogo from "../../assets/images/buchi-logo.png";
import BuchiLogoSmall from "../../assets/images/buchi-logo-small.png";

const mainTabsAccessors = {
    dashboard: "dashboard",
    lablake: "lablake",
    materials: "materials",
    scansManagement: "scansManagement",
    labstore: "labstore",
    modelDevelopment: "modelDevelopment",
    accessManagement: "accessManagement",
    options: "options",
};

const SideMenu = () => {
    const [sideMenuOpen, setSideMenuOpen] = useState(false);
    const [fullMenu, setFullMenu] = useState(true);
    const [roles, setRoles] = useState([]);
    const { user: currentUser } = useContext(UserContext);

    const [expandedTabs, setExpandedTabs] = useState({});
    const navigate = useNavigate();
    const mainPathname = kebabToCamelCase(getURIParts());

    useEffect(() => {
        if (!isEmpty(currentUser)) {
            setRoles(getRoles());
            setExpandedTabs(
                Object.keys(mainTabsAccessors).reduce((prev, current) => {
                    const collapsedTabs = {
                        [mainTabsAccessors.options]: true,
                    };
                    return { ...prev, [current]: !collapsedTabs[current] };
                }, {})
            );
        }
    }, [currentUser]);

    const menuRef = useClickOutside(() => {
        setSideMenuOpen(false);
    });

    const toggleSideMenu = () => {
        setSideMenuOpen(!sideMenuOpen);
    };

    const isActive = (path) => {
        if (window.location.href.includes(path)) {
            return "active";
        }
        return "";
    };

    const closeSideMenu = (path, isExternal) => {
        if (isExternal) handleExternalLink(path);
        else navigate(path);
        setSideMenuOpen(false);
    };

    const toggleFullMenu = () => {
        setFullMenu(!fullMenu);
    };

    const isFeedmillOrganization = () =>
        currentUser?.organization?.type === ORGANIZATION_TYPES.Feedmill;

    const topTabs = [
        {
            accessor: mainTabsAccessors.dashboard,
            name: t("dashboard"),
            key: "dashboard",
            icon: "home",
            subTabs: [
                {
                    name: t("overview"),
                    key: "overview",
                    icon: "chart-line",
                    path: DASHBOARD_ROUTES.Overview,
                },
                {
                    name: t("analytics"),
                    key: "analytics",
                    icon: "chart-pie",
                    path: DASHBOARD_ROUTES.Analytics,
                },
            ],
        },
        {
            accessor: mainTabsAccessors.lablake,
            name: t("lablake"),
            key: "lablake",
            icon: "vial",
            roles: [
                ROLES.partnerUser,
                ROLES.user,
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.support,
            ],
            hide: !currentUser?.organization?.access?.labLakeAccess,
            subTabs: [
                {
                    name: t("analytics"),
                    key: "analytics",
                    icon: "chart-pie",
                    path: LABLAKE_ROUTES.Dashboard.Analytics,
                },
                {
                    name: t("instruments"),
                    key: "instruments",
                    icon: "microchip",
                    path: LABLAKE_ROUTES.Instruments.List,
                },
                {
                    name: t("materials"),
                    key: "materials",
                    icon: "leaf",
                    path: LABLAKE_ROUTES.Materials.List,
                },
                {
                    name: t("scans"),
                    key: "scans",
                    icon: "flask",
                    path: LABLAKE_ROUTES.Scans.List,
                },
                {
                    name: t("alerts"),
                    key: "alerts",
                    icon: "exclamation-triangle",
                    path: LABLAKE_ROUTES.Alerts.List,
                    roles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    name: t("fileUploads"),
                    key: "fileUploads",
                    icon: "upload",
                    path: LABLAKE_ROUTES.LabLakeFileUploads,
                },
            ],
        },
        {
            name: t("labStore"),
            icon: "store",
            key: "labStore",
            path: LABSTORE_ROUTES.Store,
            hide: !isLabstoreAuthorized(currentUser, LABSTORE_ACCESS.view),
            roles: [ROLES.partnerAdmin, ROLES.support],
        },
        {
            name: t("subscribedProducts"),
            key: "subscribedProducts",
            icon: "cubes",
            path: LABSTORE_ROUTES.SubscribedProducts,
            roles: [
                ROLES.partnerAdmin,
                ROLES.support,
                ROLES.admin,
                ROLES.partnerUser,
                ROLES.user,
            ],
            hide: !isLabstoreAuthorized(currentUser, LABSTORE_ACCESS.subscribe),
            tippyContent: t("subscribedModelsTippy"),
        },
        {
            name: t("measurements"),
            key: "measurements",
            icon: "chart-bar",
            path: MODEL_DEVELOPMENT_ROUTES.Measurements,
            roles: [
                ROLES.admin,
                ROLES.partnerUser,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
            tippyContent: t("measurementsTippy"),
        },
        {
            name: t("models"),
            key: "models",
            icon: "atom",
            path: MODEL_DEVELOPMENT_ROUTES.Models.List,
            tippyContent: t("modelsTippy"),
        },
        {
            name: t("scans"),
            key: "scans",
            icon: "flask",
            path: SCANS_MANAGEMENT_ROUTES.Scans.List,
            tippyContent: t("scansTippy"),
        },
        {
            name: t("affiliates"),
            key: "affiliates",
            icon: "building",
            path: ACCESS_MANAGEMENT_ROUTES.Affiliates.List,
            roles: [ROLES.partnerAdmin, ROLES.superAdmin, ROLES.support],
        },
        {
            name: t("headerUser"),
            key: "user",
            icon: "user-friends",
            path: ACCESS_MANAGEMENT_ROUTES.Users.List,
            roles: [
                ROLES.admin,
                ROLES.partnerAdmin,
                ROLES.superAdmin,
                ROLES.support,
            ],
            tippyContent: t("usersTippy"),
        },
        {
            accessor: mainTabsAccessors.options,
            name: t("options"),
            key: "options",
            icon: "cogs",
            subTabs: [
                {
                    name: t("alerts"),
                    key: "alerts",
                    icon: "exclamation-triangle",
                    path: SCANS_MANAGEMENT_ROUTES.Alerts.List,
                    roles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    name: t("materialGroups"),
                    key: "materialGroups",
                    icon: "layer-group",
                    path: MATERIALS_ROUTES.MaterialGroups,
                    roles: [ROLES.support, ROLES.partnerAdmin],
                    tippyContent: t("materialGroupsTippy"),
                },
                {
                    name: t("lotManagement"),
                    key: "lotManagement",
                    icon: "map-marker-alt",
                    path: MATERIALS_ROUTES.Lots,
                    tippyContent: t("lotManagementTippy"),
                },
                {
                    name: t("publishedProducts"),
                    key: "publishedProducts",
                    icon: "cloud",
                    path: LABSTORE_ROUTES.PublishedProducts.List,
                    roles: [ROLES.partnerAdmin, ROLES.support],
                    hide: !isLabstoreAuthorized(
                        currentUser,
                        LABSTORE_ACCESS.publish
                    ),
                },
                {
                    name: t("modelTransfer"),
                    key: "modelTransfer",
                    icon: "exchange-alt",
                    path: OPTIONS_ROUTES.ModelTransfer,
                    roles: [ROLES.support],
                },
                {
                    name: t("suppliers"),
                    key: "suppliers",
                    icon: "box-open",
                    path: MATERIALS_ROUTES.MaterialsSuppliers,
                    hide: !currentUser?.organization?.access?.suppliersAccess,
                },
                {
                    name: t("coupons"),
                    key: "coupons",
                    icon: "ticket-alt",
                    path: LABSTORE_ROUTES.Coupons,
                    roles: [ROLES.partnerAdmin, ROLES.support],
                    hide: !currentUser?.organization?.access
                        ?.createCouponAccess,
                },
                {
                    name: isFeedmillOrganization()
                        ? t("shipments")
                        : t("batches"),
                    key: "shipments",
                    icon: "truck",
                    path: isFeedmillOrganization()
                        ? EXTERNAL_ROUTES.Feedmill.Shipments
                        : EXTERNAL_ROUTES.Feedmill.Batches,
                    isExternal: true,
                },
                {
                    name: t("unitsManagement"),
                    key: "unitsManagement",
                    icon: "percentage",
                    path: OPTIONS_ROUTES.UnitsManagement,
                    roles: [ROLES.superAdmin],
                },
                {
                    name: t("classifiersManagement"),
                    key: "classifiersManagement",
                    icon: "object-group",
                    path: OPTIONS_ROUTES.ClassifiersManagement,
                    roles: [ROLES.support, ROLES.partnerAdmin],
                },
                {
                    name: t("deviceManagement"),
                    key: "deviceManagement",
                    icon: "microchip",
                    path: OPTIONS_ROUTES.DeviceManagement,
                    roles: [ROLES.partnerAdmin, ROLES.support],
                },

                {
                    name: t("notificationManagement"),
                    key: "notificationManagement",
                    icon: "bell",
                    path: OPTIONS_ROUTES.Notifications.List,
                    roles: [ROLES.superAdmin],
                },
                {
                    name: t("apiKeys"),
                    icon: "key",
                    key: "apiKeys",
                    path: OPTIONS_ROUTES.ApiKeys,
                    hide: !currentUser?.organization?.access?.apiKeyAccess,
                    roles: [ROLES.partnerAdmin, ROLES.support],
                },
                {
                    name: t("activityFeed"),
                    key: "activityFeed",
                    icon: "rss",
                    path: OPTIONS_ROUTES.ActivityFeed,
                    roles: [ROLES.superAdmin, ROLES.support],
                    tippyContent: t("activityFeedTippy"),
                },
            ],
        },
    ];

    const bottomTabs = [
        // {
        //     name: "Settings",
        //     icon: "cog",
        //     path: "/settings",
        // },
        {
            name: t("helpAndSupportSideMenu"),
            key: "helpAndSupportSideMenu",
            icon: "question",
            path: GLOBAL_ROUTES.Support,
            // callback: (event) => {
            //     window.location = "mailto:support@si-ware.com";
            //     event.preventDefault();
            // },
        },
    ];

    const handleCollapseTabs = (name) => {
        setExpandedTabs({ ...expandedTabs, [name]: !expandedTabs[name] });
    };

    const SideMenuWrapper = ({ tippyContent, children }) => {
        if (tippyContent) {
            return (
                <Tippy
                    content={tippyContent}
                    placement="right"
                    animation="scale"
                >
                    {children}
                </Tippy>
            );
        }
        return children;
    };

    const renderSubTabItem = ({ tab }) => (
        <div className="collapsed-link" key={tab.key}>
            <SideMenuWrapper tippyContent={tab.tippyContent}>
                <li
                    className={`flex flex-col ${isActive(tab.path)}`}
                    onClick={() => handleCollapseTabs(tab.accessor)}
                    id={`side-menu-tab-${tab.key}`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center">
                            <i className={`fas fa-${tab.icon}`} />
                            <span className="ml-2-5">{tab.name}</span>
                        </div>
                        <div
                            className={`fas fa-chevron-right collapsed-icon text-xxs ${
                                expandedTabs[tab.accessor] ? "rotated" : ""
                            }`}
                        ></div>
                    </div>
                </li>
            </SideMenuWrapper>

            <ul
                className={`sub-sidebar-menu ${
                    expandedTabs[tab.accessor] || tab.accessor === mainPathname
                        ? "show"
                        : ""
                }`}
            >
                {tab.subTabs?.map((subTab) => {
                    if (
                        (!subTab.roles || isAuthorized(subTab.roles, roles)) &&
                        !subTab.hide
                    ) {
                        return (
                            <SideMenuWrapper
                                key={`sub-tab-${subTab.key}`}
                                tippyContent={subTab.tippyContent}
                            >
                                <li
                                    className={`flex gap-3 items-center ${isActive(
                                        subTab.path
                                    )}`}
                                    onClick={() =>
                                        closeSideMenu(
                                            subTab.path,
                                            subTab.isExternal
                                        )
                                    }
                                    id={`side-menu-tab-${tab.key}-subtab-${subTab.key}`}
                                >
                                    <i className={`fas fa-${subTab.icon}`}></i>
                                    <span className="text-left">
                                        {subTab.name}
                                    </span>
                                </li>
                            </SideMenuWrapper>
                        );
                    }
                })}
            </ul>
        </div>
    );

    const renderTabs = (tabs) =>
        tabs
            ?.filter((tab) => tab)
            .map((tab) => {
                if (
                    (!tab.roles || isAuthorized(tab.roles, roles)) &&
                    !tab.hide
                ) {
                    if (tab.subTabs) {
                        return renderSubTabItem({ tab });
                    }
                    return (
                        <SideMenuWrapper
                            key={tab.key}
                            tippyContent={tab.tippyContent}
                        >
                            <li
                                className={`flex items-center ${isActive(
                                    tab.path
                                )}`}
                                onClick={() => closeSideMenu(tab.path)}
                                id={`side-menu-tab-${tab.key}`}
                            >
                                <i className={`fas fa-${tab.icon}`} />
                                <Link to={tab.path}>{tab.name}</Link>
                            </li>
                        </SideMenuWrapper>
                    );
                }
            });

    const renderBottomItems = () =>
        bottomTabs?.map((tab) => {
            if (!tab.hide) {
                return (
                    <SideMenuWrapper
                        key={tab.key}
                        tippyContent={tab.tippyContent}
                    >
                        <li
                            className={`flex items-center ${isActive(
                                tab.path
                            )}`}
                            onClick={() => closeSideMenu(tab.path)}
                        >
                            <i className={`fas fa-${tab.icon}`} />
                            <Link to={tab.path} onClick={tab.callback}>
                                {tab.name}
                            </Link>
                        </li>
                    </SideMenuWrapper>
                );
            }
        });

    return (
        <>
            <aside
                ref={menuRef}
                className={`main-sidebar ${
                    fullMenu ? "uncollapse" : "collapsed"
                } ${sideMenuOpen ? "show" : ""}`}
                id="main-sidebar"
            >
                <div
                    className="toggle-menu-btn toggle-menu-full"
                    data-element="main-sidebar"
                    onClick={toggleFullMenu}
                >
                    <i
                        className={`fas fa-chevron-${
                            fullMenu ? "left" : "right"
                        }`}
                    />
                </div>
                <div
                    className="toggle-menu-btn toggle-menu-mobile"
                    data-element="main-sidebar"
                    onClick={toggleSideMenu}
                >
                    <i
                        className={
                            "fas fa-cog " + (sideMenuOpen ? "fa-spin" : "")
                        }
                    />
                </div>
                <div className="flex flex-col justify-between h-full small-scroll-y overflow-auto overflow-x-hidden">
                    <ul className="sidebar-menu--left-list">
                        {renderTabs(topTabs)}
                    </ul>
                    <div>
                        <ul className="sidebar-menu-left-helper !py-0 !my-0">
                            {renderBottomItems()}
                        </ul>
                        <div className="sidebar-menu-left-footer border-t-2 border-solid border-gray-200 py-5 flex justify-center">
                            <img
                                src={fullMenu ? BuchiLogo : BuchiLogoSmall}
                                alt="Buchi Logo"
                                className={fullMenu ? "h-9" : "h-6"}
                            />
                        </div>
                    </div>
                </div>
            </aside>
        </>
    );
};

export default SideMenu;
